import { formatMoney } from '../../components/data-grid/money';
import { sheetOf } from '../../components/report-grid/export';
import { ReportGrid } from '../../components/report-grid/ReportGrid';
import type { ReportGridColumn } from '../../components/report-grid/ReportGrid';
import { useI18n } from '../../i18n/I18nProvider';
import type { Language } from '../../i18n/catalogs';
import type { AccountAnalysisGroup, AccountAnalysisReport } from './reports.api';
import { ReportFrame } from './ReportFrame';
import { formatPercent, groupShare, reportSheetName } from './totals';

export interface AccountAnalysisViewProps {
  report: AccountAnalysisReport;
  language: Language;
  periodLabel: string;
  accountLabel: string;
}

export function AccountAnalysisView({
  report,
  language,
  periodLabel,
  accountLabel,
}: AccountAnalysisViewProps): JSX.Element {
  const { t } = useI18n();

  const columns: Array<ReportGridColumn<AccountAnalysisGroup>> = [
    {
      key: 'value',
      header: t('reportsDimension'),
      render: (group) =>
        group.value === null ? (
          <span className="muted">{t('reportsUnassigned')}</span>
        ) : (
          <span className="report__group-name">{group.value}</span>
        ),
      exportValue: (group) => group.value ?? '',
    },
    {
      key: 'opening',
      header: t('reportsOpening'),
      align: 'right',
      numeric: true,
      render: (group) => formatMoney(group.opening, language),
      exportValue: (group) => group.opening,
    },
    {
      key: 'turnoverDt',
      header: t('reportsDebit'),
      align: 'right',
      numeric: true,
      render: (group) => (group.turnoverDt === '0.0000' ? '—' : formatMoney(group.turnoverDt, language)),
      exportValue: (group) => group.turnoverDt,
    },
    {
      key: 'turnoverCt',
      header: t('reportsCredit'),
      align: 'right',
      numeric: true,
      render: (group) => (group.turnoverCt === '0.0000' ? '—' : formatMoney(group.turnoverCt, language)),
      exportValue: (group) => group.turnoverCt,
    },
    {
      key: 'closing',
      header: t('reportsBalance'),
      align: 'right',
      numeric: true,
      render: (group) => formatMoney(group.closing, language),
      exportValue: (group) => group.closing,
    },
    {
      key: 'lines',
      header: t('reportsLines'),
      align: 'right',
      numeric: true,
      render: (group) => String(group.lineCount),
      exportValue: (group) => String(group.lineCount),
    },
    {
      key: 'share',
      header: t('reportsShare'),
      align: 'right',
      render: (group) => formatPercent(groupShare(group, report.totals), language),
      exportValue: (group) => groupShare(group, report.totals).toFixed(1),
    },
  ];

  const sheet = sheetOf(
    reportSheetName('account-analysis', report),
    columns.map((column) => ({
      header: column.header,
      numeric: column.numeric,
      exportValue: column.exportValue,
    })),
    report.groups,
    [
      t('reportsTotals'),
      report.totals.opening,
      report.totals.turnoverDt,
      report.totals.turnoverCt,
      report.totals.closing,
      String(report.groups.reduce((sum, group) => sum + group.lineCount, 0)),
      '100.0',
    ],
  );

  return (
    <ReportFrame
      title={t('reportsAccountAnalysis')}
      subtitle={accountLabel}
      periodLabel={periodLabel}
      sheet={sheet}
    >
      <p className="report__note">
        {t('reportsGrouping')}: {report.by}
      </p>
      <ReportGrid
        columns={columns}
        rows={report.groups}
        getRowKey={(group) => group.value ?? 'unassigned'}
        totals={[
          t('reportsTotals'),
          formatMoney(report.totals.opening, language),
          formatMoney(report.totals.turnoverDt, language),
          formatMoney(report.totals.turnoverCt, language),
          formatMoney(report.totals.closing, language),
          String(report.groups.reduce((sum, group) => sum + group.lineCount, 0)),
          formatPercent(100, language),
        ]}
        emptyText={t('reportsEmpty')}
        testId="account-analysis"
      />
    </ReportFrame>
  );
}
